// src/theme/createAppTheme.js
import { createTheme } from '@mui/material/styles';
import { BRAND_COLORS } from './brandColors';

const HEADER_BG = BRAND_COLORS.red;
const HEADER_FG = BRAND_COLORS.white;

const sharedTypography = {
    fontFamily: 'Roboto, Arial, sans-serif',
    fontSize: 14,
    fontWeightMedium: 500,
    h2: {
        fontSize: '1.5rem',
        fontWeight: 600,
    },
};

const sharedComponents = {
    MuiCssBaseline: {
        styleOverrides: {
            'html, body, #root': { height: '100%' },
            '#root': { display: 'flex', flexDirection: 'column' },
        },
    },

    MuiTableCell: {
        styleOverrides: {
            root: { padding: '10px 13px' },
            head: ({ theme }) => ({
                backgroundColor: theme.palette.tableHeader.main,
                color: theme.palette.tableHeader.contrastText,
                borderBottom: `1px solid ${theme.palette.divider}`,
            }),
        },
    },

    MuiTableRow: {
        styleOverrides: {
            root: { transition: 'background-color 0.2s ease' },
        },
    },

    // Darker outline on filled/standard banners (matches green EntitySaveNotice).
    MuiAlert: {
        styleOverrides: {
            standardSuccess: ({ theme }) => ({
                border: '1px solid',
                borderColor: theme.palette.success.dark,
            }),
            standardInfo: ({ theme }) => ({
                border: '1px solid',
                borderColor: theme.palette.info.dark,
            }),
            standardWarning: ({ theme }) => ({
                border: '1px solid',
                borderColor: theme.palette.warning.dark,
            }),
            standardError: ({ theme }) => ({
                border: '1px solid',
                borderColor: theme.palette.error.dark,
            }),
        },
    },

    MuiButton: {
        defaultProps: { variant: 'contained', color: 'primary', disableElevation: true },
        styleOverrides: {
            root: {
                borderRadius: 10,
                paddingInline: 20,
                paddingBlock: 6,
                minHeight: 40,
                lineHeight: 1.4,
                textTransform: 'none',
            },
            containedPrimary: ({ theme }) => ({
                backgroundColor: theme.palette.primary.main,
                '&:hover': { backgroundColor: theme.palette.primary.dark },
            }),
        },
    },

    MuiChip: {
        defaultProps: {
            clickable: false,
            onClick: () => {},
        },
    },

    MuiTextField: {
        defaultProps: { size: 'small', variant: 'outlined' },
        styleOverrides: {
            root: { '& .MuiInputBase-root': { borderRadius: 10 } },
        },
        variants: [
            {
                props: { variant: 'search' },
                style: ({ theme }) => ({
                    '& .MuiInputBase-root': { paddingRight: theme.spacing(1) },
                }),
            },
        ],
    },

    MuiFormLabel: {
        styleOverrides: {
            root: ({ theme }) => ({
                color: theme.palette.text.secondary,
                fontWeight: 500,
                marginBottom: theme.spacing(0.75),
                // Keep radio/fieldset legends from turning brand-red when an option is selected.
                '&.Mui-focused': {
                    color: theme.palette.text.secondary,
                },
            }),
        },
    },

    MuiInputLabel: {
        styleOverrides: {
            root: ({ theme }) => ({
                '&.Mui-focused': {
                    color: theme.palette.primary.main,
                },
            }),
        },
    },

    MuiCard: {
        styleOverrides: {
            root: {
                borderRadius: 16,
            },
        },
    },

    MuiDataGrid: {
        defaultProps: {
            rowHeight: 44,
            columnHeaderHeight: 44,
            disableRowSelectionOnClick: true,
        },
        styleOverrides: {
            root: ({ theme }) => ({
                border: 0,
                '--DataGrid-overlayHeight': '240px',

                '& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton, & .MuiDataGrid-filterIcon, & .MuiDataGrid-iconButtonContainer': {
                    color: theme.palette.tableHeader.contrastText,
                },
            }),

            row: ({ theme }) => ({
                transition: 'background-color 0.2s ease',
                '&.grid-row-even': {
                    backgroundColor: theme.palette.action.hover,
                },
                '&.grid-row-odd': {
                    backgroundColor: 'inherit',
                },
                '&:hover': {
                    backgroundColor: theme.palette.action.selected,
                    cursor: 'pointer',
                    '@media (hover: none)': {
                        backgroundColor: 'inherit',
                    },
                },
            }),

            cell: ({ theme }) => ({
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',

                '&.MuiDataGrid-cell--pinnedLeft, &.MuiDataGrid-cell--pinnedRight': {
                    backgroundColor: theme.palette.background.paper,
                },
            }),
        },
    },
};

function lightPalette() {
    return {
        mode: 'light',
        primary: {
            main: BRAND_COLORS.red,
            light: '#ff4d5e',
            dark: BRAND_COLORS.redDark,
            contrastText: BRAND_COLORS.white,
        },
        secondary: {
            main: BRAND_COLORS.gray,
            light: '#9e9b9f',
            dark: BRAND_COLORS.black,
            contrastText: BRAND_COLORS.white,
        },
        text: {
            primary: BRAND_COLORS.black,
            secondary: BRAND_COLORS.gray,
        },
        background: {
            default: BRAND_COLORS.grayLight,
            paper: BRAND_COLORS.white,
        },
        action: {
            hover: 'rgba(163, 155, 156, 0.11)',
            selected: 'rgba(163, 155, 156, 0.26)',
            disabled: 'rgba(0,0,0,0.26)',
            disabledBackground: 'rgba(0,0,0,0.12)',
        },
        brand: BRAND_COLORS,
        tableHeader: {
            main: HEADER_BG,
            contrastText: HEADER_FG,
        },
        DataGrid: {
            bg: '#ffffff',
            pinnedBg: '#fff',
            headerBg: HEADER_BG,
        },
    };
}

function darkPalette() {
    return {
        mode: 'dark',
        primary: {
            main: BRAND_COLORS.red,
            light: '#ff4d5e',
            dark: BRAND_COLORS.redDark,
            contrastText: BRAND_COLORS.white,
        },
        secondary: {
            main: '#a8a4a9',
            light: '#c4c1c5',
            dark: '#7d7a7e',
            contrastText: BRAND_COLORS.black,
        },
        text: {
            primary: '#f5f5f5',
            secondary: '#b0adb1',
        },
        background: {
            default: '#121212',
            paper: '#1e1e1e',
        },
        action: {
            hover: 'rgba(255, 255, 255, 0.08)',
            selected: 'rgba(255, 255, 255, 0.16)',
            disabled: 'rgba(255,255,255,0.3)',
            disabledBackground: 'rgba(255,255,255,0.12)',
        },
        brand: BRAND_COLORS,
        tableHeader: {
            main: HEADER_BG,
            contrastText: HEADER_FG,
        },
        DataGrid: {
            bg: '#1e1e1e',
            pinnedBg: '#1e1e1e',
            headerBg: HEADER_BG,
        },
    };
}

/**
 * @param {'light' | 'dark'} mode
 */
export function createAppTheme(mode = 'light') {
    const resolved = mode === 'dark' ? 'dark' : 'light';
    return createTheme({
        palette: resolved === 'dark' ? darkPalette() : lightPalette(),
        typography: sharedTypography,
        components: sharedComponents,
    });
}
